import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import ReactFlagsSelect from "react-flags-select";
import { Link } from "react-router-dom";
import i18n from "../../i18n";
import { useTranslation } from "react-i18next";
import {
  languageBaseCode,
  languageFlagSrc,
  languageLabelKey,
} from "../../utils/languageDisplay";
import "../../css/footer.scss";


const LANG_TO_COUNTRY = {
  en: "GB",
  nl: "NL",
  de: "DE",
  fr: "FR"
};

const ENTRY_LANG_OPTIONS = [
  { code: "en", labelKey: "footer.langEnglish" },
  { code: "nl", labelKey: "footer.langDutch" },
  { code: "de", labelKey: "footer.langGerman" },
  { code: "fr", labelKey: "footer.langFrench" },
];

const SiteSettingsContext = createContext(null);

export function useSiteSettings() {
  const ctx = useContext(SiteSettingsContext);
  if (!ctx) {
    throw new Error("useSiteSettings must be used within SiteSettingsProvider");
  }
  return ctx;
}

export function SiteSettingsProvider({ children }) {
  const { t } = useTranslation();

  const toTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const [lang, setLang] = useState(i18n.language || "de");

  useEffect(() => {
    const sync = () => setLang(i18n.language || "de");
    i18n.on("languageChanged", sync);
    return () => i18n.off("languageChanged", sync);
  }, []);

  const [entryGateOpen, setEntryGateOpen] = useState(() => {
    try {
      return localStorage.getItem("entryGateAccepted") !== "1";
    } catch {
      return true;
    }
  });

  const [ageConfirmed, setAgeConfirmed] = useState(true);
  const [deliveryCountry, setDeliveryCountry] = useState("DE");
  const [researchChecked, setResearchChecked] = useState(true);
  const [gateLangOpen, setGateLangOpen] = useState(false);

  useEffect(() => {
    if (!entryGateOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [entryGateOpen]);

  const canContinueEntryGate =
    Boolean(deliveryCountry) &&
    ageConfirmed === true &&
    researchChecked === true;

  const handleEntryContinue = () => {
    if (!canContinueEntryGate) return;
    try {
      localStorage.setItem("entryGateAccepted", "1");
      localStorage.setItem("deliveryCountry", deliveryCountry);
    } catch { }
    setEntryGateOpen(false);
  };

  const openSettings = useCallback(() => {
    try {
      if (localStorage.getItem("entryGateAccepted") === "1") {
        const dc = localStorage.getItem("deliveryCountry");
        if (dc) setDeliveryCountry(dc);
        setAgeConfirmed(true);
        setResearchChecked(true);
      }
    } catch { }
    setGateLangOpen(false);
    setEntryGateOpen(true);
  }, []);

  const value = { openSettings, settingsModalOpen: entryGateOpen };

  return (
    <SiteSettingsContext.Provider value={value}>
      {children}
      {entryGateOpen && (
        <div
          className="footer__entryOverlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="entry-gate-title"
        >
          <div className="footer__entryModal">
            {ageConfirmed === false ? (
              <div className="entryModal__denied">
                <div className="entryModal__deniedIcon" aria-hidden>
                  🚫
                </div>
                <h2 className="entryModal__deniedTitle" id="entry-gate-title">
                  {t("footer.entryGate.accessDeniedTitle")}
                </h2>
                <p className="entryModal__deniedText">
                  {t("footer.entryGate.accessDeniedBody")}
                </p>
              </div>
            ) : (
              <>
                <svg width="0" height="0" style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }} aria-hidden="true">
                  <filter id="logo-white-to-dark-settings" colorInterpolationFilters="sRGB">
                    <feColorMatrix 
                      type="matrix" 
                      values="1 0 -1.221 0 0.321
                              0 1 -1.221 0 0.321
                              0 0 -0.221 0 0.321
                              0 0 0 1 0" 
                    />
                  </filter>
                </svg>
                <img className="entryModal__logoImg" alt="" src="/head/head-new.png"  />
                <h2 className="entryModal__title" id="entry-gate-title">
                  {t("footer.entryGate.title")}
                </h2>
                <p className="entryModal__subtitle">
                  {t("footer.entryGate.subtitle")}
                </p>

                <fieldset className="entryModal__fieldset">
                  <legend className="entryModal__legend">
                    {t("footer.entryGate.deliveryCountry")}
                  </legend>
                  <div className="entryModal__controlShell">
                    <ReactFlagsSelect
                      selected={deliveryCountry}
                      onSelect={(code) => {
                        setDeliveryCountry(code);
                        setGateLangOpen(false);
                      }}
                      customLabels={{
                        NL: t("footer.entryGate.countries.NL"),
                        BE: t("footer.entryGate.countries.BE"),
                        DE: t("footer.entryGate.countries.DE"),
                        FR: t("footer.entryGate.countries.FR"),
                        GB: t("footer.entryGate.countries.GB"),
                      }}
                      placeholder={t("footer.entryGate.searchCountry")}
                      className="entryModal__flags"
                      selectButtonClassName="entryModal__flagsBtn"
                      searchable
                      searchPlaceholder={t("footer.entryGate.searchCountry")}
                      alignOptionsToRight={false}
                      showSelectedLabel={true}
                      showOptionLabel={true}

                    />
                    <img
                      className="entryModal__flagsChevron"
                      alt=""
                      src="/head/arrow1.svg"
                      aria-hidden
                    />
                  </div>
                </fieldset>

                <fieldset className="entryModal__fieldset">
                  <legend className="entryModal__legend">
                    {t("footer.entryGate.language")}
                  </legend>
                  <div
                    className={`entryModal__select ${gateLangOpen ? "open" : ""
                      }`}
                  >
                    <button
                      type="button"
                      className="entryModal__selectBtn"
                      aria-expanded={gateLangOpen}
                      onClick={() => {
                        setGateLangOpen((v) => !v);
                      }}
                    >
                      <img
                        alt=""
                        src={languageFlagSrc(lang)}
                        className="entryModal__langImg"
                      />
                      <span>{t(languageLabelKey(lang))}</span>
                      <img
                        className="entryModal__chevron"
                        alt=""
                        src="/head/arrow1.svg"
                      />
                    </button>
                    {gateLangOpen && (
                      <ul
                        className="entryModal__dropdown entryModal__dropdown--lang"
                        role="listbox"
                      >
                        {ENTRY_LANG_OPTIONS.map(({ code, labelKey }) => {
                          const active = languageBaseCode(lang) === code;
                          return (
                            <li
                              key={code}
                              role="option"
                              aria-selected={active}
                              className={`entryModal__listItem ${active ? "active" : ""
                                }`}
                              onClick={() => {
                                setLang(code);
                                try {
                                  localStorage.setItem("lang", code);
                                } catch { }
                                i18n.changeLanguage(code);
                                setGateLangOpen(false);
                                const mappedCountry = LANG_TO_COUNTRY[code];
                                if (mappedCountry) {
                                  setDeliveryCountry(mappedCountry);
                                }
                              }}
                            >
                              <img
                                alt=""
                                src={languageFlagSrc(code)}
                                className="entryModal__langImg"
                              />
                              {t(labelKey)}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                </fieldset>

                <fieldset className="entryModal__fieldset entryModal__fieldset--age">
                  <legend className="entryModal__legend">
                    {t("footer.entryGate.ageQuestion")}
                  </legend>
                  <div className="entryModal__ageRow">
                    <button
                      type="button"
                      className={`entryModal__ageYes ${ageConfirmed === true ? "active" : ""
                        }`}
                      onClick={() => setAgeConfirmed(true)}
                    >
                      {t("footer.entryGate.ageYes")}
                    </button>
                    <button
                      type="button"
                      className={`entryModal__ageNo ${ageConfirmed === false ? "active" : ""
                        }`}
                      onClick={() => setAgeConfirmed(false)}
                    >
                      {t("footer.entryGate.ageNo")}
                    </button>
                  </div>
                </fieldset>

                <label className="entryModal__checkRow" htmlFor="entry-gate-research">
                  <input
                    id="entry-gate-research"
                    type="checkbox"
                    checked={researchChecked}
                    onChange={(e) => setResearchChecked(e.target.checked)}
                  />
                  <span>{t("footer.entryGate.researchLabel")}</span>
                </label>

                <button
                  type="button"
                  className="entryModal__continue"
                  disabled={!canContinueEntryGate}
                  aria-disabled={!canContinueEntryGate}
                  onClick={handleEntryContinue}
                >
                  {t("footer.entryGate.continue")}
                </button>

                <p className="entryModal__cookie">
                  {t("footer.entryGate.cookiePrefix")}
                  <Link
                    className="entryModal__privacyLink"
                    to="/privacy"
                    onClick={toTop}
                  >
                    {t("footer.entryGate.privacyPolicy")}
                  </Link>
                </p>
              </>
            )}
          </div>
        </div>
      )}
    </SiteSettingsContext.Provider>
  );
}
